import { apiGet, apiPost } from "./api";

/** Mirrors the backend SongSyncState enum. */
export type SongSyncState = "SYNCED" | "PENDING_ADD" | "PENDING_REMOVE" | "EXCLUDED";

export const SONG_STATE_LABELS: Record<SongSyncState, string> = {
    SYNCED: "In your library",
    PENDING_ADD: "Will add",
    PENDING_REMOVE: "Will remove",
    EXCLUDED: "Skipped",
};

export const SONG_STATES: SongSyncState[] = ["SYNCED", "PENDING_ADD", "PENDING_REMOVE", "EXCLUDED"];

export interface SyncSongDTO {
    id: number;
    title: string;
    artist: string;
    imageUrl: string;
    source: string;
    /** Only set on toRemove/manualRemovals entries - the provider's own track id. */
    externalTrackId?: string | null;
}

export interface SyncPreviewDTO {
    // Song.id - in the Shufl playlist, not yet in your external playlist.
    toAdd: SyncSongDTO[];
    // PlaylistLinkSyncedSong.id - to come back out of your external playlist.
    toRemove: SyncSongDTO[];
    // SongIdentity.id - in your external playlist, not yet in the Shufl playlist.
    // Importing these makes them visible to every collaborator.
    incoming: SyncSongDTO[];
    // Song.id - songs you originally added and have since removed; committing
    // drops them from the shared playlist for everyone.
    masterDeletions: SyncSongDTO[];
    // False for providers whose API can't remove tracks (Apple Music has no
    // delete-from-playlist endpoint). toRemove is still real and worth showing -
    // it just can't be applied automatically when this is false.
    removalSupported: boolean;
}

export interface SyncCommitResultDTO {
    addedCount: number;
    removedCount: number;
    notFound: string[];
    // Removals the user selected that the provider couldn't actually apply
    // (removalSupported was false). Additions still went through.
    manualRemovals: SyncSongDTO[];
    importedCount: number;
    deletedFromPlaylistCount: number;
}

export function fetchSyncPreview(playlistId: number, provider: string): Promise<SyncPreviewDTO> {
    return apiGet<SyncPreviewDTO>(`/v1/sync/preview?playlistId=${playlistId}&provider=${encodeURIComponent(provider)}`);
}

export function commitSync(
    playlistId: number,
    provider: string,
    addSongIds: number[],
    removeSyncedIds: number[],
    incomingIdentityIds: number[] = [],
    masterDeleteSongIds: number[] = []
): Promise<SyncCommitResultDTO> {
    return apiPost<SyncCommitResultDTO>("/v1/sync/commit", {
        playlistId,
        provider,
        addSongIds,
        removeSyncedIds,
        incomingIdentityIds,
        masterDeleteSongIds,
    });
}

/** Song identity id -> state. Songs with no stored state are absent from the map. */
export function fetchSongStates(playlistId: number, provider: string): Promise<Record<string, SongSyncState>> {
    return apiGet<Record<string, SongSyncState>>(
        `/v1/sync/songStates?playlistId=${playlistId}&provider=${encodeURIComponent(provider)}`
    );
}

export function setSongState(
    playlistId: number,
    provider: string,
    songIdentityId: number,
    state: SongSyncState
): Promise<{ state: SongSyncState }> {
    return apiPost<{ state: SongSyncState }>("/v1/sync/songState", {
        playlistId,
        provider,
        songIdentityId,
        state,
    });
}

export interface SyncUsageDTO {
    used: number;
    limit: number;
    remaining: number;
}

/** How much of today's sync allowance is left. */
export function fetchSyncUsage(): Promise<SyncUsageDTO> {
    return apiGet<SyncUsageDTO>("/v1/sync/usage");
}

/**
 * Fired after a commit so the header's counter updates without waiting for a reload.
 * Mirrors PLAYLISTS_CHANGED_EVENT - the counter lives in the header, several levels
 * away from the sync UI with no shared ancestor holding this state.
 */
export const SYNC_USAGE_CHANGED_EVENT = "shufl:sync-usage-changed";

export function notifySyncUsageChanged(): void {
    window.dispatchEvent(new Event(SYNC_USAGE_CHANGED_EVENT));
}
